// Panel 5 — Resumen del turno (mockup "Resumen del turno"): donut de
// cumplimiento + 4 tarjetas de estado de planta.

import type { PantallaDatos } from 'schemas';
import { colorPct, ETIQUETA, fmt, TituloPanel } from '../shared';

export function PanelTurno({ datos, baseLabel }: { datos: PantallaDatos; baseLabel: string }) {
  const r = datos.resumen;
  const pct = r.cumplimiento_pct;

  return (
    <section className="flex h-full flex-col gap-6 px-[4vw] py-[3vh]">
      <TituloPanel
        titulo="Resumen del turno"
        subtitulo={`Totales de planta en curso · ${datos.planta.nombre} · meta según ${baseLabel}`}
      />
      <div className="grid min-h-0 flex-1 grid-cols-[minmax(18rem,26rem)_1fr] items-center gap-[4vw]">
        <div className="flex flex-col items-center gap-4">
          <Donut pct={pct} />
          <p
            className="text-[clamp(1rem,1.3vw,1.4rem)] text-fg"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            <span className="font-medium">{fmt.format(r.producido)}</span>
            <span className="text-fg-tertiary">
              {' '}
              / {r.esperado > 0 ? fmt.format(r.esperado) : '—'} paq
            </span>
          </p>
        </div>
        <div className="grid grid-cols-2 gap-[1.4vw]">
          <Tarjeta
            label="LÍNEAS EN META"
            valor={String(r.lineas_en_meta)}
            sufijo={` / ${r.lineas_activas}`}
            detalle="≥ 90% del objetivo del turno"
            color={r.lineas_en_meta > 0 ? 'var(--ok)' : 'var(--fg)'}
          />
          <Tarjeta
            label="LÍNEAS EN RIESGO"
            valor={String(r.lineas_riesgo)}
            sufijo={` / ${r.lineas_activas}`}
            detalle="por debajo del 70%"
            color={r.lineas_riesgo > 0 ? 'var(--crit)' : 'var(--ok)'}
          />
          <Tarjeta
            label="TIEMPO DE PARO"
            valor={fmt.format(r.minutos_paro)}
            sufijo=" min"
            detalle="acumulado en el turno"
            color={r.minutos_paro >= 120 ? 'var(--warn)' : 'var(--fg)'}
          />
          <Tarjeta
            label="FALTANTE PARA META"
            valor={fmt.format(r.faltante)}
            sufijo=" paq"
            detalle="restante del objetivo del turno"
            color="var(--fg)"
          />
        </div>
      </div>
    </section>
  );
}

function Donut({ pct }: { pct: number | null }) {
  const size = 300;
  const stroke = 22;
  const radio = (size - stroke) / 2;
  const circ = 2 * Math.PI * radio;
  const avance = pct === null ? 0 : Math.min(100, pct) / 100;
  return (
    <div className="relative" style={{ width: 'clamp(220px, 22vw, 320px)' }}>
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="w-full"
        role="img"
        aria-label="Cumplimiento del turno"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radio}
          fill="none"
          stroke="var(--surface-overlay)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radio}
          fill="none"
          stroke={colorPct(pct)}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${circ * avance} ${circ}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          className="transition-[stroke-dasharray] duration-700 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`${ETIQUETA} tracking-[0.14em] text-fg-tertiary`}>CUMPLIMIENTO</span>
        <span
          className="font-medium leading-none text-fg"
          style={{ fontSize: 'clamp(2.6rem, 4.4vw, 4.6rem)', fontVariantNumeric: 'tabular-nums' }}
        >
          {pct === null ? '—' : Math.round(pct)}
          <span className="text-[0.45em] text-fg-secondary">%</span>
        </span>
        <span className={`${ETIQUETA} tracking-[0.14em] text-fg-tertiary`}>
          DE LA META DEL TURNO
        </span>
      </div>
    </div>
  );
}

function Tarjeta({
  label,
  valor,
  sufijo,
  detalle,
  color,
}: {
  label: string;
  valor: string;
  sufijo: string;
  detalle: string;
  color: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-surface-elevated px-[1.6vw] py-[2.6vh]">
      <p className={`${ETIQUETA} tracking-[0.14em] text-fg-tertiary`}>{label}</p>
      <p className="mt-3 leading-none" style={{ fontVariantNumeric: 'tabular-nums' }}>
        <span className="text-[clamp(2rem,3.2vw,3.4rem)] font-medium" style={{ color }}>
          {valor}
        </span>
        <span className="text-[clamp(0.95rem,1.3vw,1.4rem)] text-fg-tertiary">{sufijo}</span>
      </p>
      <p className="mt-3 text-[0.85rem] text-fg-secondary">{detalle}</p>
    </div>
  );
}
